import React, { useEffect, useRef, useState } from 'react';
import './index.scss';
type ControlerBarProps = {
  duration: number;
  currentTime: number;
  handleEventPlay: AnyFunc;
  handleEventPause: AnyFunc;
  handleControlChange: AnyFunc;
  isPaused: Boolean;
};

const formatTime = (time: number | undefined) => {
  if (!time && typeof time !== 'number') {
    return '';
  }
  if (time <= 0) {
    return '00:00:00';
  }
  const hours = Math.floor(time / 1000 / 3600);
  const minutes = Math.floor(time / 1000 / 60) % 60;
  const seconds = Math.floor((time / 1000) % 60);

  function format(v: number) {
    return v >= 10 ? v : `0${v}`;
  }

  return `${format(hours)}:${format(minutes)}:${format(seconds)}`;
};

const ControlBar = (props: ControlerBarProps) => {
  const {
    currentTime,
    duration,
    handleEventPlay,
    handleEventPause,
    isPaused,
    handleControlChange,
  } = props;

  const trackElement = useRef<HTMLDivElement>(null);
  const hoverTimeElement = useRef<HTMLDivElement>(null);
  const seeking = useRef(false);
  const [seekHoverPosition, setSeekHoverPos] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const hoverTimeClassName =
    seekHoverPosition > 0 || seeking.current
      ? 'hover-time  active'
      : 'hover-time';
  const [pageX, setPageX] = useState(0); // 鼠标点击的水平位置
  const setTrackWidthState = () => {
    if (trackElement.current) {
      setTrackWidth(trackElement.current.offsetWidth);
    }
  };
  // 处理进度条跳转
  useEffect(() => {
    if (seeking.current) {
      changeCurrentTimePosition(pageX);
    }
  }, [pageX]);

  // 绑定事件
  useEffect(() => {
    setTrackWidthState();

    window.addEventListener('resize', setTrackWidthState);
    window.addEventListener('mouseup', mouseSeekingHandler);

    return () => {
      window.removeEventListener('resize', setTrackWidthState);
      window.removeEventListener('mouseup', mouseSeekingHandler);
    };
  }, [duration, trackWidth]);

  // 获取鼠标hover位置的时间
  const getHoverTime = (
    seekHoverPosition: number,
    trackWidth: number,
    duration: number
  ) => {
    const percent = (seekHoverPosition * 100) / trackWidth;
    const hoverTime = formatTime(Math.floor(+(percent * (duration / 100))));
    return hoverTime;
  };
  const hoverTimeStr = getHoverTime(seekHoverPosition, trackWidth, duration);

  // 获取鼠标hover的位置
  const getHoverTimePosition = (
    seekHoverPosition: number,
    hoverTimeElement: HTMLDivElement | null,
    trackWidth: number
  ): { transform: string } => {
    let position = 0;

    if (hoverTimeElement) {
      position = seekHoverPosition - hoverTimeElement.offsetWidth / 2;

      if (position < 0) {
        position = 0;
      } else if (position + hoverTimeElement.offsetWidth > trackWidth) {
        position = trackWidth - hoverTimeElement.offsetWidth;
      }
    }

    return { transform: `translateX(${position}px)` };
  };
  const hoverTimePos = getHoverTimePosition(
    seekHoverPosition,
    hoverTimeElement?.current,
    trackWidth
  );

  // 改变进度条当前播放时间
  const changeCurrentTimePosition = (pageX: number) => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    let position = pageX - left;

    position = position < 0 ? 0 : position;
    position = position > trackWidth ? trackWidth : position;

    setSeekHoverPos(position);

    const percent = (position * 100) / trackWidth;
    const time = +(percent * (duration / 100)).toFixed(0);

    handleControlChange(time);
  };

  const handleTrackHover = (
    clear: boolean,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    const position = clear ? 0 : event.pageX - left;

    setSeekHoverPos(position);
  };

  const setSeeking = (state: boolean, event: MouseEvent) => {
    event.preventDefault();
    setPageX(event.pageX);
    seeking.current = state;
    setSeekHoverPos(state ? seekHoverPosition : 0);
  };

  const mouseSeekingHandler = (event: MouseEvent) => {
    setSeeking(false, event);
  };

  // 快退功能（后退10秒）
  const handleRewind = () => {
    const newTime = Math.max(0, currentTime - 10000); // 减少10秒（10000毫秒）
    handleControlChange(newTime);
  };

  // 快进功能（前进10秒）
  const handleForward = () => {
    const newTime = Math.min(duration, currentTime + 10000); // 增加10秒（10000毫秒）
    handleControlChange(newTime);
  };

  return (
    <div className="modern-control-bar">
      <div className="control-content">
        <div className="progress-container">
          <div
            className="progress-track"
            ref={trackElement}
            onMouseMove={(event) => handleTrackHover(false, event)}
            onMouseLeave={(event) => handleTrackHover(true, event)}
            onMouseDown={(event) => setSeeking(true, event as any)}
          >
            <div className="progress-bg"></div>
            <div
              className="progress-played"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            ></div>
            <div
              className="progress-hover"
              style={{
                width: `${(seekHoverPosition / trackWidth) * 100}%`,
                opacity: seekHoverPosition > 0 ? 1 : 0,
              }}
            ></div>
            <div
              className="progress-thumb"
              style={{ left: `${(currentTime / duration) * 100}%` }}
            ></div>
          </div>

          {/* 悬停时间提示 */}
          <div
            className={hoverTimeClassName}
            style={hoverTimePos}
            ref={hoverTimeElement}
          >
            {hoverTimeStr}
          </div>
        </div>
        <div className="time-display">
          {formatTime(currentTime)} /{' '}
          {duration !== 0 ? formatTime(duration) : '00:00:00'}
        </div>
      </div>
      <div className="control-buttons">
        <button className="control-btn rewind-btn" onClick={handleRewind}>
          <svg
            width="25"
            height="18"
            viewBox="0 0 25 18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="#FFF" fill-rule="evenodd">
              <path d="M18 0H0v18h18z" fill-opacity=".5" opacity=".01" />
              <path
                d="M11.806 12.403V5.208A1.5 1.5 0 0 0 9.318 4.08L5.207 7.677a1.5 1.5 0 0 0 0 2.257l4.11 3.598a1.5 1.5 0 0 0 2.489-1.13z"
                fill-rule="nonzero"
              />
              <g>
                <path d="M25 0H7v18h18z" fill-opacity=".5" opacity=".01" />
                <path
                  d="M18.806 12.403V5.208a1.5 1.5 0 0 0-2.488-1.129l-4.111 3.598a1.5 1.5 0 0 0 0 2.257l4.11 3.598a1.5 1.5 0 0 0 2.489-1.13z"
                  fill-rule="nonzero"
                />
              </g>
            </g>
          </svg>
        </button>

        <button
          className={`control-btn play-pause-btn ${!isPaused ? 'playing' : ''}`}
          onClick={!isPaused ? handleEventPause : handleEventPlay}
        >
          {!isPaused ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
            </svg>
          )}
        </button>

        <button className="control-btn forward-btn" onClick={handleForward}>
          <svg
            width="25"
            height="18"
            viewBox="0 0 25 18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="#FFF" fill-rule="evenodd">
              <path d="M7 0h18v18H7z" fill-opacity=".5" opacity=".01" />
              <path
                d="M13.194 12.403V5.208a1.5 1.5 0 0 1 2.488-1.129l4.111 3.598a1.5 1.5 0 0 1 0 2.257l-4.11 3.598a1.5 1.5 0 0 1-2.489-1.13z"
                fill-rule="nonzero"
              />
              <g>
                <path d="M0 0h18v18H0z" fill-opacity=".5" opacity=".01" />
                <path
                  d="M6.194 12.403V5.208A1.5 1.5 0 0 1 8.682 4.08l4.111 3.598a1.5 1.5 0 0 1 0 2.257l-4.11 3.598a1.5 1.5 0 0 1-2.489-1.13z"
                  fill-rule="nonzero"
                />
              </g>
            </g>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default React.memo(ControlBar);
